import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { CircularProgress, Grid, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import LangEnum from '../../lang/LangEnum';

import { useApplicationContext } from '../../modules/context/ApplicationContext/ApplicationContext';
import { Seller } from '../../modules/customer/types/customer.types';
import { myNetworkExploreRepository } from '../../modules/my-network/explore/myNetworkExplore.repository';
import { useTranslation } from '../../modules/translation/TranslationContext';
import SellerOption from '../MyNetwork/SellerOption';
import PrivacyPolitics from '../PrivacyPolitics';
import AutocompleteMuiAdapter from '../adaptersMui/AutocompleteMuiAdapter/AutocompleteMuiAdapter';

type ConnectModalProps = {
  open: boolean;
  brandOrStoreName: string;
  brandOrStoreId: string;
  isSubmitting: boolean;
  onClose: (success?: boolean) => void;
  onConnect: (message: string, sellers?: string[] | string[], destinationId?: string) => void;
};

const ConnectModal = ({
  open,
  isSubmitting,
  brandOrStoreName,
  brandOrStoreId,
  onClose,
  onConnect,
}: ConnectModalProps) => {
  const [message, setMessage] = useState<string>('');
  const [sellers, setSellers] = useState<Array<Seller>>([]);
  const [autocompleteOptions, setAutocompleteOptions] = useState<Array<Seller>>([]);
  const [loading, setIsLoading] = useState(true);
  const { translate } = useTranslation();
  const { userRole: role, state, isSeller } = useApplicationContext();

  const isStore = role ? ['COMPRADOR', 'ADMIN_LOJA'].includes(role) : false;

  const handleClose = (success: boolean) => {
    setMessage('');
    setIsLoading(false);
    setSellers([]);
    setAutocompleteOptions([]);
    onClose(success);
  };

  const handleSubmit = () => {
    const sellersMap = sellers.map((seller) => seller.userId);
    if (isStore || isSeller) {
      onConnect(message, state.user?.userId.split(', '), brandOrStoreId);
    } else {
      onConnect(message, sellersMap, brandOrStoreId);
    }
  };

  const loadSellers = useCallback(async () => {
    setIsLoading(true);
    const response = await myNetworkExploreRepository().getSellers();
    setAutocompleteOptions(response?.data?.data || []);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!isStore && open) {
      loadSellers();
    }
  }, [isStore, loadSellers, open]);

  return (
    <Modal
      isOpen={open}
      size="standard"
      header={{ title: translate(LangEnum.MY_NETWORK_CONNECT_MODAL_TITLE, { brandOrStoreName }) }}
      onClose={() => handleClose(false)}
      footer={{
        primaryAction: {
          label: LangEnum.MY_NETWORK_SUGGESTIONS_LIST_CONNECT,
          isDisabled: isSubmitting,
          onClick: handleSubmit,
        },
        secondaryAction: { label: LangEnum.GLOBAL_CANCEL_LABEL, onClick: () => handleClose(false) },
      }}
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          {isStore || isSeller ? (
            <Typography color="textSecondary">
              {translate(LangEnum.MY_NETWORK_CONNECT_MODAL_MESSAGE)}
            </Typography>
          ) : (
            <AutocompleteMuiAdapter
              multiple
              options={autocompleteOptions}
              disableCloseOnSelect
              filterSelectedOptions
              loadingText={translate(LangEnum.GLOBAL_LOADING)}
              noOptionsText="Não encontrado"
              getOptionLabel={(option: any) => option.name || '-'}
              isOptionEqualToValue={(option, value) => option.email === value.email}
              value={sellers as Array<any>}
              loading={loading}
              onChange={(_, items: Array<any>) => setSellers(items)}
              renderOption={(option: any, { selected }) => (
                <SellerOption
                  selected={selected}
                  surname={option.surname}
                  name={option.name}
                  email={option.email}
                  profileUrl={option.profileUrl}
                />
              )}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  label="Digite o nome do vendedor"
                  placeholder="vendedores"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          )}
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            variant="outlined"
            label={translate(LangEnum.CATALOGS_SHARE_MODAL_MESSAGE_BOX_TITLE)}
            multiline
            rows={3}
            value={message}
            onChange={(event: any) => setMessage(event.target.value)}
          />
        </Grid>

        <Grid size={12}>
          <PrivacyPolitics
            label={translate(LangEnum.MY_NETWORK_CONNECT_PRIVACY_POLICE, {
              storeOrBrand: isStore
                ? translate(LangEnum.GLOBAL_BRAND)
                : translate(LangEnum.GLOBAL_STORE),
            })}
            fontSize="medium"
            actionLabel=""
          />
        </Grid>
      </Grid>
    </Modal>
  );
};

export default ConnectModal;
